/**
 * Hook principal do Welcome Wizard
 * Gerencia estado completo, persistência em localStorage, navegação e ações de dados
 * 
 * API PÚBLICA (publicar no dia 2):
 * - wizard.state: estado completo
 * - wizard.currentStepKey: string da tela atual
 * - wizard.stepIndex: número base 1 para barra de progresso
 * - wizard.template: tema carregado
 * - wizard.goNext() / wizard.goBack() / wizard.rebuildSteps()
 * - wizard.setTheme / setUserName / setLocationName / setProfessionals / setServices / etc
 * - wizard.submit() / wizard.completeAndExit()
 * - wizard.showSkip / wizard.revealSkip() / wizard.skipWizard()
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { loadTemplate } from '../utils/templateLoader';
import {
  postWelcome,
  getWelcomeSummary,
  buildWelcomePayload,
  validateWelcomePayload,
} from '../services/welcomeService';
import { authService } from '../services/authService';
import { wlog, wsnapshot } from '../utils/welcomeDebug';
import { readWelcomeSession, writeWelcomeSession } from '../utils/welcomeSession';

/**
 * Monta o array de steps dinamicamente após a Tela 3 (equipe confirmada).
 * skipThemeStep (D7/D8, onda 3): omite a Tela 0 quando o usuário já nasceu com tema
 * (utm_campaign válido no cadastro, 3B.2) — começa direto no step seguinte.
 * Exportada (além do hook) só para ser testável em isolamento — função pura, sem estado.
 */
export const buildSteps = (professionals, hasGroups, skipThemeStep = false) => {
  const isTeam = professionals.length > 1;
  return [
    ...(skipThemeStep ? [] : ['theme']),                 // Tela 0 (omitida se já tem tema)
    'userName',                                          // n=1
    'locationName',                                      // n=2
    'team',                                              // n=3
    'services',                                          // n=4
    'servicePricing',                                    // n=5 (D1)
    ...(isTeam ? professionals.map((_, i) => `assign_${i}`) : []),  // Tela 6 — só equipe
    'groupQuestion',
    ...(hasGroups ? ['groupSizes'] : []),
    ...professionals.map((_, i) => `schedule_${i}`),     // Tela 8
    'processing',
    'summary',
  ];
};

/**
 * Decide se a Tela 0 (seleção de tema) pode ser omitida.
 *
 * Ter tema NÃO basta: 'default' é o "Outros", o único tema cujo `customBusinessType`
 * (descrição do negócio) é OBRIGATÓRIO no POST /api/welcome — e a Tela 0 é o único lugar
 * onde esse campo é capturado. Quem já tem theme_id='default' no banco (o PUT /users/theme
 * da Tela 0, ou a troca de ramo pelo menu) recebe o claim `theme=default` no próximo login;
 * pular a Tela 0 nesse caso fazia o usuário percorrer o wizard INTEIRO e levar 400
 * ("Ao selecionar 'Outros', o campo 'customBusinessType' é obrigatório") na última tela,
 * sem nenhuma tela onde corrigir. Exportada para ser testável em isolamento.
 */
export const canSkipThemeStep = (themeId, customBusinessType = null) =>
  !!themeId && (themeId !== 'default' || !!customBusinessType);

/**
 * Estado inicial do wizard.
 * D7/D8 (onda 3): se o usuário já nasceu com tema (claim do JWT — UserService grava na
 * criação via utm_campaign, 3B.2), pré-popula themeId e pula a Tela 0.
 */
const createInitialState = () => {
  const existingThemeId = authService.getThemeFromToken();
  return {
    themeId: existingThemeId || null,
    customBusinessType: null,
    userName: '',
    locationName: '',
    professionals: [],
    services: [],
    serviceDetails: {},
    hasGroups: false,
    groupSizes: {},
    steps: buildSteps([], false, canSkipThemeStep(existingThemeId)),
    currentStepIndex: 0,
    showSkip: false,
    isProcessing: false,
    result: null,
    error: null,
    completedAt: null,
  };
};

/**
 * Hook principal do Welcome Wizard
 */
export const useWelcomeWizard = () => {
  const [state, setState] = useState(createInitialState);
  const [template, setTemplate] = useState(null);
  const templateLoadedRef = useRef(false);

  // ───── DEBUG: loga TODO render do hook ─────
  wlog('useWelcomeWizard', 'RENDER', {
    ...wsnapshot(state),
    templateLoaded: !!template,
    templateId: template?.id,
  });

  // Carrega o template ao montar
  useEffect(() => {
    wlog('useWelcomeWizard', 'EFEITO template', {
      themeId: state.themeId,
      jaCarregado: templateLoadedRef.current,
    });
    const loadTheme = async () => {
      if (state.themeId && !templateLoadedRef.current) {
        try {
          const tpl = await loadTemplate(state.themeId);
          wlog('useWelcomeWizard', 'template CARREGADO', { themeId: tpl?.id, temWelcome: !!tpl?.welcome });
          setTemplate(tpl);
          templateLoadedRef.current = true;
        } catch (error) {
          wlog('useWelcomeWizard', '⚠️ ERRO ao carregar template', error?.message);
          console.error('[useWelcomeWizard] Erro ao carregar template:', error);
        }
      }
    };
    loadTheme();
  }, [state.themeId]);

  // Restaura estado do localStorage ao montar.
  // readWelcomeSession() só devolve a sessão se pertencer ao usuário atual —
  // sessão de outra conta (mesmo navegador) é ignorada (não vaza tema/step).
  useEffect(() => {
    const parsed = readWelcomeSession();
    wlog('useWelcomeWizard', 'EFEITO restore — localStorage', parsed || 'VAZIO');
    if (parsed) {
      try {
        if (parsed.completedAt) {
          wlog('useWelcomeWizard', 'restore IGNORADO (completedAt presente)', parsed.completedAt);
          return;
        }
        const podePular = canSkipThemeStep(parsed.themeId, parsed.customBusinessType);
        // Sessões gravadas ANTES deste fix podem ter o array de steps montado SEM a Tela 0
        // mesmo com themeId='default' e sem descrição — restaurar como está deixaria o
        // usuário preso no mesmo 400 para sempre. Nesse caso remontamos os steps e voltamos
        // para a Tela 0 (os dados já digitados continuam no state, as telas repõem).
        const stepsInvalidos = !podePular && !parsed.steps?.includes('theme');
        if (!parsed.steps || parsed.steps.length === 0 || stepsInvalidos) {
          parsed.steps = buildSteps(parsed.professionals || [], parsed.hasGroups || false, podePular);
          if (stepsInvalidos) {
            wlog('useWelcomeWizard', '⚠️ sessão sem Tela 0 e sem customBusinessType — remontando steps', {
              themeId: parsed.themeId,
            });
            parsed.currentStepIndex = 0;
          }
        }

        // Mesma armadilha da Tela 0, no campo do LOCAL: a sessão salva repõe `currentStepIndex`,
        // então uma sessão gravada DEPOIS da tela do local mas com `locationName` vazio devolvia o
        // usuário para um ponto adiante — a tela do local nunca mais aparecia e o submit virava 400
        // garantido. (A tela em si bloqueia avanço com nome vazio: `Step2Location` tem
        // `disabled={!isFilled}`; o vazio só chega aqui pela restauração.) Recuar para a tela do
        // local é seguro: os dados já digitados continuam no state e as telas seguintes os repõem.
        const locationVazia = !parsed.locationName || String(parsed.locationName).trim().length === 0;
        const idxLocal = parsed.steps.indexOf('locationName');
        if (locationVazia && idxLocal >= 0 && (parsed.currentStepIndex ?? 0) > idxLocal) {
          wlog('useWelcomeWizard', '⚠️ sessão restaurada além da tela do local com locationName vazio — voltando', {
            currentStepIndex: parsed.currentStepIndex,
            idxLocal,
          });
          parsed.currentStepIndex = idxLocal;
        }
        wlog('useWelcomeWizard', '⚠️ RESTAURANDO estado salvo (pode pular telas!)', wsnapshot(parsed));
        setState(parsed);
        templateLoadedRef.current = false;
      } catch (error) {
        wlog('useWelcomeWizard', '⚠️ ERRO ao restaurar', error?.message);
        console.error('[useWelcomeWizard] Erro ao restaurar estado:', error);
      }
    }
  }, []);

  // Persiste estado em localStorage a cada mudança (carimbando o userId atual).
  useEffect(() => {
    writeWelcomeSession(state);
    wlog('useWelcomeWizard', 'PERSIST → localStorage', wsnapshot(state));
  }, [state]);

  // Telemetria de passo (B-031): o wizard é uma rota só com N etapas internas, então o
  // rastreio normal de acesso emite UM evento e o funil no Dashboard só consegue medir
  // "cadastrou × concluiu" — sem saber ONDE a pessoa desiste. Aqui cada etapa vira uma
  // rota sintética em analytics.access_log (`/admin/step/{passo}`), e o funil por passo
  // sai de uma query. Falha de telemetria nunca pode atrapalhar o onboarding: o
  // trackStep já engole tudo internamente, e o optional chaining cobre o script ausente
  // (bloqueador de anúncio, offline, localhost).
  const trackedStepKey = state.steps?.[state.currentStepIndex];
  useEffect(() => {
    if (!trackedStepKey) return;
    window.AgndTrack?.trackStep?.(trackedStepKey);
  }, [trackedStepKey]);

  // ========================
  // AÇÕES DE NAVEGAÇÃO
  // ========================

  const goNext = useCallback(() => {
    wlog('useWelcomeWizard', 'goNext() CHAMADO', new Error().stack?.split('\n').slice(1, 4).join(' | '));
    setState((prev) => {
      const nextIndex = prev.currentStepIndex + 1;
      if (nextIndex < prev.steps.length) {
        wlog('useWelcomeWizard', `goNext: ${prev.currentStepIndex} → ${nextIndex}`, {
          de: prev.steps[prev.currentStepIndex],
          para: prev.steps[nextIndex],
        });
        return { ...prev, currentStepIndex: nextIndex };
      }
      wlog('useWelcomeWizard', '⚠️ goNext BLOQUEADO (fim do array)', {
        currentStepIndex: prev.currentStepIndex,
        stepsLen: prev.steps.length,
      });
      return prev;
    });
  }, []);

  const goBack = useCallback(() => {
    wlog('useWelcomeWizard', 'goBack() CHAMADO');
    setState((prev) => {
      // Não volta a partir da Tela 9 (processing/summary)
      if (
        prev.steps[prev.currentStepIndex] === 'processing' ||
        prev.steps[prev.currentStepIndex] === 'summary'
      ) {
        return prev;
      }
      const prevIndex = prev.currentStepIndex - 1;
      if (prevIndex >= 0) {
        return { ...prev, currentStepIndex: prevIndex };
      }
      return prev;
    });
  }, []);

  const rebuildSteps = useCallback(() => {
    setState((prev) => {
      const newSteps = buildSteps(prev.professionals, prev.hasGroups, !prev.steps.includes('theme'));
      return { ...prev, steps: newSteps };
    });
  }, []);

  // ========================
  // AÇÕES DE DADOS
  // ========================

  const setTheme = useCallback((themeId, customBusinessType = null) => {
    wlog('useWelcomeWizard', 'setTheme()', { themeId, customBusinessType });
    setState((prev) => ({
      ...prev,
      themeId,
      customBusinessType,
    }));
    templateLoadedRef.current = false; // força reload do template
  }, []);

  const setUserName = useCallback((name) => {
    wlog('useWelcomeWizard', 'setUserName()', name);
    setState((prev) => {
      // Profissional 0 é sempre o usuário
      const updated = { ...prev, userName: name };
      if (prev.professionals.length > 0) {
        updated.professionals = [
          { ...prev.professionals[0], name },
          ...prev.professionals.slice(1),
        ];
      }
      return updated;
    });
  }, []);

  const setLocationName = useCallback((name) => {
    setState((prev) => ({ ...prev, locationName: name }));
  }, []);

  const setProfessionals = useCallback((list) => {
    setState((prev) => ({
      ...prev,
      professionals: list,
      steps: buildSteps(list, prev.hasGroups, !prev.steps.includes('theme')),
    }));
  }, []);

  const setServices = useCallback((list) => {
    setState((prev) => ({
      ...prev,
      services: list,
      // Reseta os detalhes de serviço ao mudar a lista
      serviceDetails: {},
      groupSizes: {},
    }));
  }, []);

  const setServiceDetail = useCallback((serviceId, details) => {
    setState((prev) => ({
      ...prev,
      serviceDetails: {
        ...prev.serviceDetails,
        [serviceId]: details,
      },
    }));
  }, []);

  const setProfessionalServiceIds = useCallback((profId, serviceIds) => {
    setState((prev) => {
      const updated = { ...prev };
      if (updated.professionals[profId]) {
        updated.professionals[profId] = {
          ...updated.professionals[profId],
          serviceIds,
        };
      }
      return updated;
    });
  }, []);

  const setHasGroups = useCallback((hasGroups) => {
    setState((prev) => {
      const newSteps = buildSteps(prev.professionals, hasGroups, !prev.steps.includes('theme'));
      return {
        ...prev,
        hasGroups,
        steps: newSteps,
      };
    });
  }, []);

  const setGroupSize = useCallback((serviceId, maxGroupSize) => {
    setState((prev) => ({
      ...prev,
      groupSizes: {
        ...prev.groupSizes,
        [serviceId]: maxGroupSize,
      },
    }));
  }, []);

  const setProfessionalSchedule = useCallback((profId, schedule) => {
    setState((prev) => {
      const updated = { ...prev };
      if (updated.professionals[profId]) {
        updated.professionals[profId] = {
          ...updated.professionals[profId],
          schedule,
        };
      }
      return updated;
    });
  }, []);

  // ========================
  // PROCESSAMENTO
  // ========================

  const submit = useCallback(async () => {
    wlog('useWelcomeWizard', 'submit() CHAMADO — estado atual', wsnapshot(state));
    setState((prev) => ({ ...prev, isProcessing: true, error: null }));
    try {
      // Monta o payload e chama a API
      const payload = buildWelcomePayload(state, template);
      wlog('useWelcomeWizard', 'submit() — payload construído', payload);
      wlog('useWelcomeWizard', 'submit() — payload é Promise?', payload instanceof Promise);

      // GUARD: nunca POSTar um payload que a API recusa com certeza. Sem isto o usuário levava 400
      // na última tela e ficava SEM NENHUMA TELA onde corrigir — o erro só virava texto em
      // `state.error`. Foi assim que uma conta tentou 27x seguidas e foi abandonada vazia
      // (varredura 2026-09-10). Aqui voltamos para a tela dona do campo que falta.
      const check = validateWelcomePayload(payload, state.steps);
      if (!check.ok) {
        wlog('useWelcomeWizard', '🛑 submit() BLOQUEADO pelo guard — payload incompleto', check);
        setState((prev) => ({
          ...prev,
          isProcessing: false,
          error: check.message,
          // stepIndex === -1: o passo não está no wizard atual (não deveria acontecer, mas se
          // acontecer é melhor ficar parado mostrando o erro do que pular para um índice inválido.
          ...(check.stepIndex >= 0 ? { currentStepIndex: check.stepIndex } : {}),
        }));
        return;
      }

      let response = await postWelcome(payload);
      wlog('useWelcomeWizard', 'submit() — resposta da API', response);

      // Se 409 (conflito), tenta recuperar o resumo
      if (response.status === 'conflict') {
        try {
          const summary = await getWelcomeSummary();
          response = { status: 'created', data: summary };
        } catch (error) {
          console.error('[useWelcomeWizard] Erro ao recuperar resumo:', error);
          throw new Error('Falha ao recuperar resumo do welcome');
        }
      }

      // Sucesso — grava resultado
      setState((prev) => ({
        ...prev,
        isProcessing: false,
        result: response.data,
        steps: buildSteps(prev.professionals, prev.hasGroups, !prev.steps.includes('theme')), // garante que 'summary' está lá
        currentStepIndex: prev.steps.indexOf('summary'),
      }));
    } catch (error) {
      console.error('[useWelcomeWizard] Erro ao submeter:', error);
      setState((prev) => ({
        ...prev,
        isProcessing: false,
        error: error.message || 'Erro ao processar welcome',
      }));
    }
  }, [state, template]);

  const completeAndExit = useCallback(() => {
    const completedAt = new Date().toISOString();
    // Write synchronously — setState is async and the useEffect that persists to
    // localStorage would never fire before window.location.href navigates away.
    try {
      const current = readWelcomeSession() || {};
      writeWelcomeSession({ ...current, completedAt });

      // Persist the selected theme so ThemeCustomProvider finds it on the next load
      // and doesn't show the legacy ThemeSelectionModal.
      const themeId = current.themeId || state.themeId;
      if (themeId) {
        const userInfo = authService.getUserInfo();
        const themeKey = userInfo?.userId ? `agendario_theme_${userInfo.userId}` : 'agendario_theme';
        localStorage.setItem(themeKey, themeId);
      }
    } catch (_) {
      writeWelcomeSession({ completedAt });
    }
    setState((prev) => ({ ...prev, completedAt }));
    return true;
  }, [state.themeId]);

  // ========================
  // SKIP (D5)
  // ========================

  const revealSkip = useCallback(() => {
    setState((prev) => ({ ...prev, showSkip: true }));
  }, []);

  const skipWizard = useCallback(() => {
    const completedAt = new Date().toISOString();
    try {
      const current = readWelcomeSession() || {};
      writeWelcomeSession({ ...current, completedAt });
      // Even on skip, persist the theme if one was selected
      const themeId = current.themeId || state.themeId;
      if (themeId) {
        const userInfo = authService.getUserInfo();
        const themeKey = userInfo?.userId ? `agendario_theme_${userInfo.userId}` : 'agendario_theme';
        localStorage.setItem(themeKey, themeId);
      }
    } catch (_) {
      writeWelcomeSession({ completedAt });
    }
    setState((prev) => ({ ...prev, completedAt }));
    return true;
  }, [state.themeId]);

  // ========================
  // COMPUTED
  // ========================

  const currentStepKey = state.steps[state.currentStepIndex] || null;
  const stepIndex =
    currentStepKey === 'processing' || currentStepKey === 'summary'
      ? 100 // Força 100% para essas telas
      : state.currentStepIndex; // Base 1 — theme está no índice 0

  wlog('useWelcomeWizard', 'COMPUTED', {
    currentStepIndex: state.currentStepIndex,
    currentStepKey,
    stepIndex,
    stepsLen: state.steps.length,
  });

  // ========================
  // API PÚBLICA
  // ========================

  return {
    // --- LEITURA DE ESTADO ---
    state,
    currentStepKey,
    stepIndex,
    template,

    // --- AÇÕES DE NAVEGAÇÃO ---
    goNext,
    goBack,
    rebuildSteps,

    // --- AÇÕES DE DADOS ---
    setTheme,
    setUserName,
    setLocationName,
    setProfessionals,
    setServices,
    setServiceDetail,
    setProfessionalServiceIds,
    setHasGroups,
    setGroupSize,
    setProfessionalSchedule,

    // --- PROCESSAMENTO ---
    submit,
    completeAndExit,

    // --- SKIP (D5) ---
    showSkip: state.showSkip,
    revealSkip,
    skipWizard,
  };
};
